import i18n from '../../plugins/i18n';

/** 예전 리소스 서버 경로 — ssolvit.com SPA로 404 폴백되어 반복 요청만 발생 */
const LEGACY_BROKEN_IMAGE_URL_PATTERN = /\/from\?get(?:&|$)/i;

/** 로컬 devServer 프록시 경로 (vue.config.js /cdn-proxy → cdn.ssolvit.com) */
export const CDN_PROXY_PREFIX = '/cdn-proxy';

const DEFAULT_CDN_HOST = 'https://cdn.ssolvit.com';

const warnedLegacyImageUrls = new Set();

/** 로컬 개발 호스트 (localhost / 127.0.0.1) — CDN 프록시·실험 UI 게이트 */
export function isLocalDevHost() {
  if (typeof window === 'undefined') {
    return false;
  }
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

function getCdnHosts() {
  const cdnUrl = process.env.VUE_APP_CDN_URL;
  const hosts = [cdnUrl, DEFAULT_CDN_HOST].filter(
    (u) => typeof u === 'string' && u.startsWith('http')
  );
  return [...new Set(hosts)];
}

function toCdnProxyPath(absoluteOrPathUrl, host) {
  const proxyPrefix = process.env.VUE_APP_CDN_PROXY_PATH || CDN_PROXY_PREFIX;
  const path = absoluteOrPathUrl.includes(host)
    ? absoluteOrPathUrl.slice(absoluteOrPathUrl.indexOf(host) + host.length)
    : absoluteOrPathUrl;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${proxyPrefix}${normalizedPath}`;
}

function toAbsoluteCdnUrl(pathOrUrl) {
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
    return pathOrUrl;
  }
  const cdnBase = process.env.VUE_APP_CDN_URL || DEFAULT_CDN_HOST;
  const normalizedBase = cdnBase.endsWith('/') ? cdnBase.slice(0, -1) : cdnBase;
  const normalizedPath = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${normalizedBase}${normalizedPath}`;
}

/**
 * DB에 남아 있는 레거시 imgUrl (…/from?get&nocache=…) 여부
 * @param {string} imageUrl
 * @returns {boolean}
 */
export function isLegacyBrokenImageUrl(imageUrl) {
  return typeof imageUrl === 'string' && LEGACY_BROKEN_IMAGE_URL_PATTERN.test(imageUrl);
}

function warnLegacyImageUrlOnce(imageUrl) {
  if (warnedLegacyImageUrls.has(imageUrl)) {
    return;
  }
  warnedLegacyImageUrls.add(imageUrl);
  console.warn(
    '[imageLoader] 레거시 imgUrl은 로드하지 않습니다. DB img_url을 CDN 경로로 갱신해 주세요:',
    imageUrl
  );
}

/**
 * CDN URL을 브라우저에서 로드 가능한 형태로 정규화한다.
 * - 로컬: /cdn-proxy (devServer 프록시, CORS 회피)
 * - 운영: CDN 절대 URL 그대로 (<img> 표시용, crossorigin 불필요)
 * @param {string} imageUrl - 원본 이미지 URL
 * @returns {string} 처리된 이미지 URL (레거시 URL이면 '')
 */
export function processImageUrlForCdn(imageUrl) {
  if (!imageUrl) {
    return imageUrl;
  }

  if (isLegacyBrokenImageUrl(imageUrl)) {
    warnLegacyImageUrlOnce(imageUrl);
    return '';
  }

  const proxyPrefix = process.env.VUE_APP_CDN_PROXY_PATH || CDN_PROXY_PREFIX;
  if (imageUrl.startsWith(proxyPrefix)) {
    return imageUrl;
  }

  const useProxy = isLocalDevHost();
  const cdnHosts = getCdnHosts();

  for (const host of cdnHosts) {
    if (!imageUrl.includes(host)) {
      continue;
    }
    if (useProxy) {
      return toCdnProxyPath(imageUrl, host);
    }
    return toAbsoluteCdnUrl(imageUrl);
  }

  return imageUrl;
}

/**
 * API/DB에서 내려온 이미지 경로를 브라우저에서 로드 가능한 CDN URL로 정규화한다.
 * - 절대 URL: processImageUrlForCdn만 적용
 * - 상대 경로: VUE_APP_CDN_URL 접두 후 processImageUrlForCdn 적용
 * @param {string} imageUrl
 * @returns {string}
 */
export function resolveCdnImageUrl(imageUrl) {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return '';
  }

  const trimmed = imageUrl.trim();
  if (!trimmed) {
    return '';
  }

  if (isLegacyBrokenImageUrl(trimmed)) {
    warnLegacyImageUrlOnce(trimmed);
    return '';
  }

  if (isLocalDevHost()) {
    const proxyPrefix = process.env.VUE_APP_CDN_PROXY_PATH || CDN_PROXY_PREFIX;
    const normalizedPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return processImageUrlForCdn(trimmed);
    }
    return `${proxyPrefix}${normalizedPath}`;
  }

  return toAbsoluteCdnUrl(trimmed);
}

/**
 * 이미지 로딩 관련 기능을 담당하는 클래스
 */
export class ImageLoader {
  /**
   * URL에서 이미지를 로드
   * @param {string} imageUrl - 이미지 URL
   * @returns {Promise<HTMLImageElement>} 로드된 이미지 객체
   */
  loadImageFromUrl(imageUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();

      const processedUrl = processImageUrlForCdn(imageUrl);
      if (!processedUrl) {
        reject(new Error('레거시 또는 비어 있는 이미지 URL'));
        return;
      }

      img.onload = () => {
        console.log(`ImageLoader: 이미지 로딩 성공 - ${img.width}x${img.height}`);
        resolve(img);
      };

      img.onerror = (error) => {
        console.error(`ImageLoader: 이미지 로딩 실패 - ${processedUrl}`, error);
        reject(new Error('이미지 로딩 실패'));
      };

      img.src = processedUrl;
    });
  }

  /**
   * 이미지 로딩 성공 여부 검증
   * @param {HTMLImageElement} img - 이미지 객체
   * @returns {boolean} 로딩 성공 여부
   */
  validateImageLoadSuccess(img) {
    return img && img.complete && img.naturalWidth > 0 && img.naturalHeight > 0;
  }

  /**
   * 이미지 크기 정보 가져오기
   * @param {HTMLImageElement} img - 이미지 객체
   * @returns {Object} 이미지 크기 정보
   */
  getImageSizeInfo(img) {
    if (!this.validateImageLoadSuccess(img)) {
      throw new Error(i18n.t('common.invalidImageObject'));
    }

    return {
      width: img.naturalWidth,
      height: img.naturalHeight,
      aspectRatio: img.naturalWidth / img.naturalHeight
    };
  }
}
